import type { Operator } from 'react-querybuilder';

// Complete list of operators supported by the backend
export const SUPPORTED_OPERATORS: Operator[] = [
  // Equality Operators
  { name: 'equals', label: 'Equals (=)' },
  { name: '=', label: 'Equals' },
  { name: 'notEquals', label: 'Not Equals (!=)' },
  { name: '!=', label: 'Not Equals' },

  // Emptiness Operators
  { name: 'isEmpty', label: 'Is Empty' },
  { name: 'isNotEmpty', label: 'Is Not Empty' },
  { name: 'null', label: 'Is Null' },
  { name: 'notNull', label: 'Is Not Null' },
  { name: 'exists', label: 'Exists' },

  // Length Operators
  { name: 'hasLength', label: 'Has Length' },

  // Comparison Operators (for numbers/dates)
  { name: 'greaterThan', label: 'Greater Than (>)' },
  { name: '>', label: 'Greater Than' },
  { name: 'lessThan', label: 'Less Than (<)' },
  { name: '<', label: 'Less Than' },
  { name: 'greaterThanOrEqual', label: 'Greater Than or Equal (>=)' },
  { name: '>=', label: 'Greater Than or Equal' },
  { name: 'lessThanOrEqual', label: 'Less Than or Equal (<=)' },
  { name: '<=', label: 'Less Than or Equal' },

  // String Operators
  { name: 'contains', label: 'Contains' },
  { name: 'notContains', label: 'Does Not Contain' },
  { name: 'regex', label: 'Matches Regex' },
  { name: 'notRegex', label: 'Does Not Match Regex' },

  // Array/List Operators
  { name: 'in', label: 'In List' },
  { name: 'notIn', label: 'Not In List' },
];

// Combinators supported by the backend
export const SUPPORTED_COMBINATORS = [
  { name: 'and', label: 'AND' },
  { name: 'or', label: 'OR' },
];

// Operator categories for different behaviors
export const OPERATOR_CATEGORIES = {
  // Operators that don't need value inputs
  NO_VALUE: ['isEmpty', 'isNotEmpty', 'null', 'notNull'],

  // Operators that need boolean inputs
  BOOLEAN: ['exists'],

  // Operators that need number inputs
  NUMBER: ['hasLength'],

  // Operators that work with lists/arrays
  LIST: ['in', 'notIn'],

  // Operators for string operations
  STRING: ['contains', 'notContains', 'equals', '=', 'notEquals', '!=', 'regex', 'notRegex'],

  // Operators for numeric/date comparisons
  COMPARISON: [
    'greaterThan',
    '>',
    'lessThan',
    '<',
    'greaterThanOrEqual',
    '>=',
    'lessThanOrEqual',
    '<=',
  ],

  // Operators that support multi-select
  MULTI_SELECT: ['contains', 'notContains', 'in', 'notIn'],
} as const;

// Utility functions
export const getOperatorCategory = (operator: string): keyof typeof OPERATOR_CATEGORIES | null => {
  if (OPERATOR_CATEGORIES.NO_VALUE.includes(operator as any)) return 'NO_VALUE';
  if (OPERATOR_CATEGORIES.BOOLEAN.includes(operator as any)) return 'BOOLEAN';
  if (OPERATOR_CATEGORIES.NUMBER.includes(operator as any)) return 'NUMBER';
  if (OPERATOR_CATEGORIES.LIST.includes(operator as any)) return 'LIST';
  if (OPERATOR_CATEGORIES.STRING.includes(operator as any)) return 'STRING';
  if (OPERATOR_CATEGORIES.COMPARISON.includes(operator as any)) return 'COMPARISON';
  if (OPERATOR_CATEGORIES.MULTI_SELECT.includes(operator as any)) return 'MULTI_SELECT';
  return null;
};

export const shouldShowValueInput = (operator: string): boolean => {
  return !(OPERATOR_CATEGORIES.NO_VALUE as readonly string[]).includes(operator);
};

export const getPlaceholderText = (
  operator: string,
  fieldLabel: string | null | undefined,
): string => {
  const category = getOperatorCategory(operator);
  const safeFieldLabel = fieldLabel || 'value';

  switch (category) {
    case 'BOOLEAN':
      return 'Select true/false';
    case 'NUMBER':
      return operator === 'hasLength' ? 'Enter length' : 'Enter number';
    case 'LIST':
      return 'Enter comma-separated values';
    case 'STRING':
      if (operator === 'contains') return 'Enter text to search for';
      if (operator === 'notContains') return 'Enter text to exclude';
      if (operator === 'equals' || operator === '=') return 'Enter text to match exactly';
      if (operator === 'notEquals' || operator === '!=') return 'Enter text to exclude';
      if (operator === 'regex') return 'Enter regular expression pattern';
      if (operator === 'notRegex') return 'Enter regular expression pattern to exclude';
      return 'Enter text';
    case 'COMPARISON':
      if (operator === 'greaterThan' || operator === '>') return `After ${safeFieldLabel}`;
      if (operator === 'lessThan' || operator === '<') return `Before ${safeFieldLabel}`;
      return 'Enter number';
    default:
      return `Enter ${safeFieldLabel}`;
  }
};

// Field type detection
export const getFieldType = (field: string, inputType?: string, schema?: any): string => {
  // Check schema first if available
  if (schema?.fields) {
    const fieldDef = schema.fields.find((f: any) => f.name === field);
    if (fieldDef?.type) return fieldDef.type;
    if (fieldDef?.inputType) return fieldDef.inputType;
  }

  // Use provided inputType
  if (inputType) return inputType;

  // Infer from field name patterns
  if (
    field.includes('date') ||
    field.includes('time') ||
    field.includes('created') ||
    field.includes('updated')
  ) {
    return 'date';
  }
  if (
    field.includes('count') ||
    field.includes('number') ||
    field.includes('age') ||
    field.includes('price') ||
    field.includes('amount')
  ) {
    return 'number';
  }
  if (field.includes('email')) return 'email';
  if (field.includes('url') || field.includes('link')) return 'url';
  if (field.includes('tags') || field.includes('categories') || field.includes('list')) {
    return 'array';
  }
  if (field.includes('active') || field.includes('enabled') || field.includes('visible')) {
    return 'boolean';
  }

  // Default to text
  return 'text';
};

// Determine if operator should use multi-select based on operator and field type
export const shouldUseMultiSelect = (operator: string, fieldType: string): boolean => {
  // List operators always use multi-select
  if (OPERATOR_CATEGORIES.LIST.includes(operator as any)) return true;

  // Multi-select operators for array fields
  if (fieldType === 'array' && OPERATOR_CATEGORIES.MULTI_SELECT.includes(operator as any))
    return true;

  return false;
};

// Get input component type based on field type and operator
export const getInputComponentType = (
  operator: string,
  fieldType: string,
  hasValues: boolean,
): 'none' | 'boolean' | 'number' | 'date' | 'dateRange' | 'multiSelect' | 'select' | 'text' => {
  const category = getOperatorCategory(operator);

  // No input for these operators
  if (category === 'NO_VALUE') return 'none';

  // Boolean input for exists operator
  if (category === 'BOOLEAN') return 'boolean';

  // Number input for length operators
  if (category === 'NUMBER') return 'number';

  // Handle date fields
  if (fieldType === 'date') {
    if (operator === 'between') return 'dateRange';
    if (category === 'COMPARISON' || category === 'STRING') return 'date';
  }

  // Handle number fields with comparison operators
  if (fieldType === 'number' && category === 'COMPARISON') return 'number';

  // Handle multi-select scenarios
  if (shouldUseMultiSelect(operator, fieldType)) return 'multiSelect';

  // Handle select with predefined values
  if (hasValues) return shouldUseMultiSelect(operator, fieldType) ? 'multiSelect' : 'select';

  // Handle list operators without predefined values
  if (category === 'LIST') return 'multiSelect';

  // Default to text input
  return 'text';
};

// Type definitions for the query structure sent to backend
export interface QueryRule {
  field: string; // Field path (can use dot notation for nested fields)
  operator: string; // One of the operators above
  value: any; // Value to compare against
}

export interface QueryRuleGroup {
  combinator: 'and' | 'or';
  rules: Array<QueryRule | QueryRuleGroup>;
}

// Example queries for documentation
export const EXAMPLE_QUERIES = {
  simple: {
    combinator: 'and',
    rules: [
      {
        field: 'status',
        operator: 'equals',
        value: 'active',
      },
    ],
  } as QueryRuleGroup,

  complex: {
    combinator: 'and',
    rules: [
      {
        field: 'status',
        operator: 'equals',
        value: 'active',
      },
      {
        field: 'user.email',
        operator: 'contains',
        value: '@company.com',
      },
      {
        field: 'tags',
        operator: 'in',
        value: ['urgent', 'bug'],
      },
      {
        combinator: 'or',
        rules: [
          {
            field: 'priority',
            operator: 'greaterThan',
            value: 5,
          },
          {
            field: 'category',
            operator: 'isEmpty',
          },
        ],
      },
    ],
  } as QueryRuleGroup,
};
